import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Lock, Mail, Loader2, Shield, ArrowLeft, Phone, Building, User, Send, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";

// Logo URLs
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [demoFormData, setDemoFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const redirectUrl = sessionStorage.getItem("redirect_after_login");
      if (redirectUrl) {
        sessionStorage.removeItem("redirect_after_login");
        navigate(redirectUrl, { replace: true });
      } else if (user.role === "maintenance_worker") {
        navigate("/worker", { replace: true });
      } else if (user.role === "trade_contractor") {
        navigate("/contractor/portal", { replace: true });
      } else if (user.role === "sales_rep") {
        navigate("/sales/dashboard", { replace: true });
      } else {
        navigate("/admin", { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        login(data.token, data.user);
        toast.success(`Welcome back, ${data.user.name}!`);
        // useEffect handles redirect based on user state change
      } else {
        toast.error(data.detail || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    setDemoLoading(true);

    try {
      const response = await fetch(`${API}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "demo_request",
          name: demoFormData.name,
          email: demoFormData.email,
          subject: `Demo Request from ${demoFormData.company || demoFormData.name}`,
          message: `
Demo Request Details:
---------------------
Name: ${demoFormData.name}
Email: ${demoFormData.email}
Phone: ${demoFormData.phone || "Not provided"}
Company: ${demoFormData.company || "Not provided"}

Additional Message:
${demoFormData.message || "No additional message"}
          `.trim()
        })
      });

      if (response.ok) {
        setDemoSubmitted(true);
        toast.success("Demo request submitted! We'll contact you soon.");
      } else {
        toast.error("Failed to submit request. Please try again.");
      }
    } catch (error) {
      console.error("Demo request error:", error);
      toast.error("Failed to connect to server");
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      {/* Header with Back Button */}
      <header className="p-4">
        <div className="max-w-6xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-5xl grid md:grid-cols-2 gap-8 items-start">
          
          {/* Left Side - Demo Request */}
          <div className="order-2 md:order-1">
            {/* Coming Soon Banner */}
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-900/30 border border-cyan-700/50 rounded-full mb-4">
                <Calendar className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-300 text-sm font-semibold">LAUNCHING FEBRUARY 2026</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Request a Demo
              </h2>
              <p className="text-slate-400">
                Interested in AskIsaac? Contact us to schedule a personalized demo of our healthcare facility management platform.
              </p>
            </div>

            {demoSubmitted ? (
              <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-xl p-6 text-center">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Request Received!</h3>
                <p className="text-slate-400 mb-4">
                  Thank you for your interest in AskIsaac. Our team will contact you within 24-48 hours to schedule your demo.
                </p>
                <Button
                  onClick={() => {
                    setDemoSubmitted(false);
                    setDemoFormData({ name: "", email: "", phone: "", company: "", message: "" });
                  }}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleDemoRequest} className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 flex items-center gap-2 mb-2">
                      <User className="w-4 h-4" />
                      Your Name *
                    </Label>
                    <Input
                      type="text"
                      placeholder="John Smith"
                      value={demoFormData.name}
                      onChange={(e) => setDemoFormData({ ...demoFormData, name: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                      required
                      data-testid="demo-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 flex items-center gap-2 mb-2">
                      <Mail className="w-4 h-4" />
                      Email Address *
                    </Label>
                    <Input
                      type="email"
                      placeholder="john@hospital.com"
                      value={demoFormData.email}
                      onChange={(e) => setDemoFormData({ ...demoFormData, email: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                      required
                      data-testid="demo-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 flex items-center gap-2 mb-2">
                      <Phone className="w-4 h-4" />
                      Phone Number
                    </Label>
                    <Input
                      type="tel"
                      placeholder="(800) 988-3892"
                      value={demoFormData.phone}
                      onChange={(e) => setDemoFormData({ ...demoFormData, phone: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                      data-testid="demo-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 flex items-center gap-2 mb-2">
                      <Building className="w-4 h-4" />
                      Organization
                    </Label>
                    <Input
                      type="text"
                      placeholder="Memorial Hospital"
                      value={demoFormData.company}
                      onChange={(e) => setDemoFormData({ ...demoFormData, company: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                      data-testid="demo-company"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300 mb-2 block">
                    Additional Information
                  </Label>
                  <Textarea
                    placeholder="Tell us about your facility and what you're looking for..."
                    value={demoFormData.message}
                    onChange={(e) => setDemoFormData({ ...demoFormData, message: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 min-h-[80px]"
                    data-testid="demo-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={demoLoading}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="demo-submit"
                >
                  {demoLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Request Demo
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  Or contact us directly at{" "}
                  <a href="mailto:info@sterilemate.com" className="text-cyan-400 hover:underline">
                    info@sterilemate.com
                  </a>
                  {" "}or{" "}
                  <a href="tel:800-988-3892" className="text-cyan-400 hover:underline">
                    800.988.3892
                  </a>
                </p>
              </form>
            )}
          </div>

          {/* Right Side - Staff Login */}
          <div className="order-1 md:order-2">
            <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-8">
              <div className="flex flex-col items-center mb-8">
                <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 p-3 flex items-center justify-center mb-4 shadow-lg border border-slate-700">
                  <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-full h-full object-contain" />
                </div>
                <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Staff Login
                </h2>
                <p className="text-slate-400 text-sm mt-2 text-center">
                  Authorized personnel only
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="text-slate-300 flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4" />
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    placeholder="your.email@sterilemate.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-emerald-500"
                    required
                    data-testid="login-email"
                  />
                </div>

                <div>
                  <Label className="text-slate-300 flex items-center gap-2 mb-2">
                    <Lock className="w-4 h-4" />
                    Password
                  </Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-emerald-500"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-cyan-400 hover:underline mt-2"
                    data-testid="admin-forgot-password"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Sign In
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-700">
                <p className="text-xs text-slate-500 text-center">
                  This portal is for authorized Sterile Mate staff only.
                  <br />
                  Contact your administrator if you need access.
                </p>
              </div>
            </div>

            {/* Portal Links */}
            <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/contractor/login" className="text-slate-400 hover:text-cyan-400 transition-colors">
                Contractor Portal →
              </Link>
              <Link to="/supplier/login" className="text-slate-400 hover:text-cyan-400 transition-colors">
                Supplier Portal →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        userType="admin"
        title="Reset Staff Password"
      />

      {/* Footer */}
      <footer className="p-6 text-center">
        <p className="text-slate-500 text-sm">
          © 2026 AskIsaac Inc. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
